
import { computed, defineComponent, onBeforeMount, reactive } from 'vue'
import { useRouter, useRoute } from 'vue-router'

// Components
import PageTitle from '@/common/components/UI/PageTitle/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'
import TheInput from '@/common/components/UI/TheInput/index.vue'
import TheSelect from '@/common/components/UI/TheSelect/index.vue'
import Checkbox from '@/common/components/UI/Checkbox/index.vue'
import Loader from '@/common/components/UI/Loader/index.vue'

// Classes
import { DataState } from '@/core/classes/DataState.class'

// Types
import { CompanyT } from '@/core/types/companies.types'
import {
    TmcReceiptItem,
    TmcT,
    TmcCreateAdmissionPayload,
    AdmissionT,
    TmcSecondaryMaterialPayload,
    TmcSecondaryMaterialItem,
} from '@/core/types/tmc.types'
import { WarehouseT } from '@/core/types/warehouses.types'

// Realisations
import { companies } from '@/core/realisations/companies.realisation'
import { tmc } from '@/core/realisations/tmc.realisation'
import { warehouses } from '@/core/realisations/warehouses.realisation'

// Validation
import useVuelidate from '@vuelidate/core'
import { required, integer, decimal, requiredIf } from '@vuelidate/validators'

// Composable
import useHelpers from '@/common/composable/useHelpers'
import useNotifications from '@/common/composable/useNotifications'
import { useRound } from '@/common/composable/useRound'

export default defineComponent({
    components: {
        'page-title': PageTitle,
        'the-button': TheButton,
        'the-input': TheInput,
        'the-select': TheSelect,
        checkbox: Checkbox,
        loader: Loader,
    },
    setup() {
        const router = useRouter()

        const route = useRoute()
        const admission_id = Number(route.params.id as string)

        const { findItemIndexById } = useHelpers()
        const notifications = useNotifications()
        const { roundTo } = useRound()

        const isClient = computed(() => item_state.tmc === 252)

        const state = reactive({
            companyId: null as number | null,
            jiraNumber: null as string | null,
            warehouseId: null as number | null,
            palletAmount: null as number | null,
            warrantNumber: null as string | null,
            //
            items: [] as {
                id?: number
                tmc: number
                companyId: number | null
                tmc_name: string
                amount: number
                unitPrice?: number
                isNds?: boolean
            }[],
            //
            is_loading: false,
            is_uncompleted_item_added: true,
            is_item_removing: false,
        })

        const admission_state = reactive({
            is_loading: false,
            is_failed: false,
            admission: null as AdmissionT | null,
        })

        const data_state = reactive({
            companies: new DataState<CompanyT>({}),
            warehouses: new DataState<WarehouseT>({}),
            tmc: new DataState<TmcT>({}),
        })

        const item_state = reactive({
            tmc: null as number | null,
            amount: null as string | null,
            companyId: null as number | null,
            unitPrice: null as string | null,
            isNds: false,
            is_edit: false,
            edit_index: null as number | null,
        })

        const itemCost = computed(() => {
            if (!item_state.amount) return 0
            if (!item_state.unitPrice) return 0

            const numered_amount = Number(item_state.amount)
            const numered_price = Number(item_state.unitPrice)

            if (isNaN(numered_amount) || isNaN(numered_price)) return 0

            return numered_amount * numered_price
        })

        const totalItemsCost = computed(() => {
            let total_cost = 0
            if (!state.items.length) return total_cost

            const item_costs = state.items.map((item) => {
                return item.unitPrice ? item.amount * item.unitPrice : 0
            })
            return item_costs.reduce((accumulator, currentValue) => accumulator + currentValue)
        })

        const isSecondMaterial = computed(() => {
            return !!admission_state.admission && admission_state.admission.isSecondMaterial
        })

        const state_validation = useVuelidate(
            {
                companyId: { required: requiredIf(() => !isSecondMaterial.value) },
                jiraNumber: { required: requiredIf(() => !isSecondMaterial.value) },
                warehouseId: { required },
                warrantNumber: { required: requiredIf(() => !isSecondMaterial.value) },
                items: { required },
                // secondary material
                palletAmount: { required: requiredIf(() => isSecondMaterial.value), integer },
            },
            state
        )

        const item_validation = useVuelidate(
            {
                tmc: { required },
                amount: { required, integer },
                companyId: { required: requiredIf(() => isClient.value) },
                unitPrice: { required: requiredIf(() => !isSecondMaterial.value), decimal },
            },
            item_state
        )

        const loadItemCompanies = async (page: number, search?: string) => {
            data_state.companies.pagination.current_page = page

            try {
                data_state.companies.is_loading = true
                const response = await companies.getList({
                    page,
                    search,
                    statusId: 2,
                    isExtended: 0,
                    limit: 9999,
                })
                data_state.companies.list = response.data

                data_state.companies.pagination.total_pages = response.meta.last_page

                data_state.companies.is_failed = false
                data_state.companies.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.companies.failure.catchError(error)

                if (!data_state.companies.failure.error_state.is_cancel) {
                    data_state.companies.is_failed = true
                }
            } finally {
                if (!data_state.companies.failure.error_state.is_cancel) {
                    data_state.companies.is_loading = false
                }
            }
        }

        // Companies
        const loadCompanies = async (page: number, search?: string) => {
            data_state.companies.pagination.current_page = page

            try {
                data_state.companies.is_loading = true
                const response = await companies.getListBilling({
                    search,
                    getAll: 1,
                    limit: 9999,
                    isExtended: 0,
                })
                data_state.companies.list = response.data

                data_state.companies.pagination.total_pages = response.meta.last_page

                data_state.companies.is_failed = false
                data_state.companies.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.companies.failure.catchError(error)

                if (!data_state.companies.failure.error_state.is_cancel) {
                    data_state.companies.is_failed = true
                }
            } finally {
                if (!data_state.companies.failure.error_state.is_cancel) {
                    data_state.companies.is_loading = false
                }
            }
        }

        const loadMoreCompanies = async (page: number) => {
            if (page > data_state.companies.pagination.total_pages) return
            data_state.companies.pagination.current_page = page

            try {
                data_state.companies.is_loading = true

                const response = await companies.getListBilling({
                    getAll: 1,
                    limit: 9999,
                    isExtended: 0,
                })
                data_state.companies.list = data_state.companies.list.concat(response.data)

                data_state.companies.pagination.total_pages = response.meta.last_page

                data_state.companies.is_failed = false
                data_state.companies.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.companies.failure.catchError(error)

                if (!data_state.companies.failure.error_state.is_cancel) {
                    data_state.companies.is_failed = true
                }
            } finally {
                if (!data_state.companies.failure.error_state.is_cancel) {
                    data_state.companies.is_loading = false
                }
            }
        }

        const searchCompanies = (search_query: string) => {
            // data_state.companies.pagination.current_page
            loadCompanies(1, search_query)
        }

        const unshiftFindedCompany = async (company: CompanyT) => {
            await loadCompanies(data_state.companies.pagination.current_page)
            data_state.companies.list.unshift(company)
        }

        // TMC
        const loadTMC = async (page: number, search?: string) => {
            data_state.tmc.pagination.current_page = page

            try {
                data_state.tmc.is_loading = true
                const response = await tmc.getList(page, search)
                data_state.tmc.list = response.data

                data_state.tmc.pagination.total_pages = response.meta.last_page

                data_state.tmc.is_failed = false
                data_state.tmc.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.tmc.failure.catchError(error)

                if (!data_state.tmc.failure.error_state.is_cancel) {
                    data_state.tmc.is_failed = true
                }
            } finally {
                if (!data_state.tmc.failure.error_state.is_cancel) {
                    data_state.tmc.is_loading = false
                }
            }
        }

        const loadMoreTMC = async (page: number) => {
            if (page > data_state.tmc.pagination.total_pages) return
            data_state.tmc.pagination.current_page = page

            try {
                data_state.tmc.is_loading = true

                const response = await tmc.getList(page)
                data_state.tmc.list = data_state.tmc.list.concat(response.data)

                data_state.tmc.pagination.total_pages = response.meta.last_page

                data_state.tmc.is_failed = false
                data_state.tmc.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.tmc.failure.catchError(error)

                if (!data_state.tmc.failure.error_state.is_cancel) {
                    data_state.tmc.is_failed = true
                }
            } finally {
                if (!data_state.tmc.failure.error_state.is_cancel) {
                    data_state.tmc.is_loading = false
                }
            }
        }

        const searchTMC = (search_query: string) => {
            loadTMC(1, search_query)
        }

        const unshiftFindedTMC = async (tmc: TmcT) => {
            await loadTMC(data_state.tmc.pagination.current_page)
            data_state.tmc.list.unshift(tmc)
        }

        // Warehouses
        const loadWarehouses = async () => {
            data_state.warehouses.pagination.current_page = 1

            try {
                data_state.warehouses.is_loading = true
                const response = await warehouses.getWarehouses()
                data_state.warehouses.list = response.data

                // data_state.warehouses.pagination.total_pages = response.meta.last_page

                data_state.warehouses.is_failed = false
                data_state.warehouses.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.warehouses.failure.catchError(error)

                if (!data_state.warehouses.failure.error_state.is_cancel) {
                    data_state.warehouses.is_failed = true
                }
            } finally {
                if (!data_state.warehouses.failure.error_state.is_cancel) {
                    data_state.warehouses.is_loading = false
                }
            }
        }

        const setAdmissionToState = () => {
            if (!admission_state.admission) return

            state.companyId = admission_state.admission.companyId
            state.jiraNumber = admission_state.admission.jiraNumber
            state.warehouseId = admission_state.admission.warehouseId
            state.warrantNumber = admission_state.admission.warrantNumber

            state.items = admission_state.admission.receiptItems.map((receiptItem) => {
                return {
                    id: receiptItem.receiptItemId,
                    tmc: receiptItem.inventoryItemId,
                    tmc_name: receiptItem.inventoryItemName,
                    amount: receiptItem.amount,
                    companyId: null,
                    unitPrice: receiptItem.unitPrice,
                    isNds: receiptItem.isNds,
                }
            })
        }

        const setAdmissionSecondaryToState = () => {
            if (!admission_state.admission) return

            state.warehouseId = admission_state.admission.warehouseId
            state.palletAmount = admission_state.admission.palletAmount

            state.items = admission_state.admission.receiptItems.map((receiptItem) => {
                return {
                    id: receiptItem.receiptItemId,
                    tmc: receiptItem.inventoryItemId,
                    tmc_name: receiptItem.inventoryItemName,
                    amount: receiptItem.amount,
                    companyId: receiptItem.companyId,
                }
            })
        }

        const loadAdmission = async () => {
            try {
                admission_state.is_loading = true

                const response = await tmc.getAdmission(admission_id)
                admission_state.admission = response.data

                if (isSecondMaterial.value) setAdmissionSecondaryToState()
                else setAdmissionToState()

                admission_state.is_failed = false
            } catch (error) {
                admission_state.is_failed = true
                notifications.notifyError({
                    error,
                    title: 'При получении поступления произошла ошибка',
                })
            } finally {
                admission_state.is_loading = false
            }
        }

        onBeforeMount(() => {
            loadAdmission()
            loadCompanies(1)
            loadItemCompanies(1)
            loadTMC(1)
            loadWarehouses()
        })

        const resetItemForm = () => {
            item_state.tmc = null
            item_state.companyId = null
            item_state.amount = null
            item_state.unitPrice = null
            item_state.isNds = false

            item_state.is_edit = false
            item_state.edit_index = null

            item_validation.value.$reset()
        }

        const addItem = () => {
            item_validation.value.$touch()
            if (item_validation.value.$invalid) return

            const index = findItemIndexById(item_state.tmc as number, data_state.tmc.list)
            if (index < 0) return

            const tmc_name = data_state.tmc.list[index].name

            const maybe_selected_tmc = state.items.find((item) => item.tmc === item_state.tmc)
            const maybe_selected_tmc_index = state.items.findIndex(
                (item) => item.tmc === item_state.tmc
            )

            if (
                (!item_state.is_edit && maybe_selected_tmc) ||
                (item_state.is_edit &&
                    maybe_selected_tmc_index !== item_state.edit_index &&
                    maybe_selected_tmc)
            ) {
                notifications.notify({
                    title: `ТМЦ '${tmc_name}' уже выбран`,
                    description: 'Пожалуйста, выберите другой ТМЦ',
                    type: 'danger',
                })
                return
            }

            const item = {
                tmc: item_state.tmc as number,
                companyId: item_state.companyId as number,
                tmc_name,
                amount: Number(item_state.amount as string),
                unitPrice: Number(item_state.unitPrice as string),
                isNds: item_state.isNds,
            }

            if (item_state.is_edit) {
                state.items[item_state.edit_index as number].tmc = item.tmc
                state.items[item_state.edit_index as number].tmc_name = item.tmc_name
                state.items[item_state.edit_index as number].amount = item.amount
                state.items[item_state.edit_index as number].unitPrice = item.unitPrice
                state.items[item_state.edit_index as number].isNds = item.isNds
                state.items[item_state.edit_index as number].companyId = item.companyId
            } else {
                state.items.push(item)
            }

            resetItemForm()
            if (!state.is_uncompleted_item_added) state.is_uncompleted_item_added = true
        }

        const removeItem = async (item_index: number) => {
            if (state.is_item_removing) return

            const answer = confirm('Вы уверены что хотите удалить товар из поступления?')
            if (!answer) return

            try {
                state.is_item_removing = true

                const item = state.items[item_index]

                if (item.id) {
                    await tmc.deleteReceiptItem(item.id)
                }

                state.items.splice(item_index, 1)
                notifications.notify({ title: 'Товар удален из поступления', type: 'success' })
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При удалении товара из поступления произошла ошибка',
                })
            } finally {
                state.is_item_removing = false
            }
        }

        const startEditItem = (item_index: number) => {
            const editable_item = state.items[item_index]

            item_state.tmc = editable_item.tmc
            item_state.amount = editable_item.amount.toString()
            if (editable_item.unitPrice) item_state.unitPrice = editable_item.unitPrice.toString()
            if (editable_item.isNds) item_state.isNds = editable_item.isNds
            item_state.companyId = editable_item.companyId

            item_state.is_edit = true
            item_state.edit_index = item_index
        }

        const mapItems = (item: {
            id?: number
            tmc: number
            tmc_name: string
            amount: number
            unitPrice?: number
            isNds?: boolean
        }): TmcReceiptItem => {
            return {
                id: item.id,
                inventoryItemId: item.tmc,
                amount: item.amount,
                unitPrice: item.unitPrice,
                isNds: item.isNds,
            }
        }

        const mapItemsSecondary = (item: {
            id?: number
            tmc: number
            tmc_name: string
            amount: number
            companyId: number | null
        }): TmcSecondaryMaterialItem => {
            return {
                id: item.id,
                inventoryItemId: item.tmc,
                amount: item.amount,
                companyId: item.companyId || null,
            }
        }

        const updateAdmission = async () => {
            const payload: TmcCreateAdmissionPayload = {
                companyId: state.companyId as number,
                jiraNumber: state.jiraNumber as string,
                warehouseId: state.warehouseId as number,
                warrantNumber: state.warrantNumber as string,
                receiptItems: state.items.map(mapItems),
            }

            await tmc.updateAdmission(admission_id, payload)
        }

        const updateAdmissionSecondary = async () => {
            const payload: TmcSecondaryMaterialPayload = {
                warehouseId: state.warehouseId as number,
                palletAmount: state.palletAmount as number,
                receiptItems: state.items.map(mapItemsSecondary),
            }

            await tmc.updateAdmissionSecondary(admission_id, payload)
        }

        const submitForm = async () => {
            if (state.is_loading) return

            if (
                state.items.length === 0 ||
                (state.items.length > 0 &&
                    (item_state.tmc || item_state.amount || item_state.unitPrice))
            ) {
                state.is_uncompleted_item_added = false
                addItem()
            }

            if (!state.is_uncompleted_item_added) {
                return
            }

            state_validation.value.$touch()

            if (state_validation.value.$invalid) {
                if (state.items.length === 0) {
                    notifications.notify({
                        title: 'Ошибка валидации',
                        description: 'Для создания заявки необходимо добавить хотя бы 1 товар',
                        type: 'danger',
                    })
                }

                return
            }

            try {
                state.is_loading = true

                if (isSecondMaterial.value) {
                    updateAdmissionSecondary()
                } else {
                    updateAdmission()
                }

                notifications.notify({ title: 'Заявка на поступление сохранена', type: 'success' })
                router.push('/tmc/admission')
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    title: 'При сохранении поступления произошла ошибка',
                    error,
                })
            } finally {
                state.is_loading = false
            }
        }

        const onTmcChange = () => {
            if (!isClient.value) {
                item_state.companyId = null
            }
        }

        return {
            state,
            data_state,
            item_state,
            admission_state,
            itemCost,
            totalItemsCost,
            //
            state_validation,
            item_validation,
            //
            loadItemCompanies,
            loadAdmission,
            loadCompanies,
            loadMoreCompanies,
            searchCompanies,
            unshiftFindedCompany,
            loadTMC,
            loadMoreTMC,
            searchTMC,
            unshiftFindedTMC,
            loadWarehouses,
            //
            addItem,
            removeItem,
            startEditItem,
            submitForm,
            roundTo,
            onTmcChange,
            //
            isSecondMaterial,
            isClient,
        }
    },
})
